import React, { useCallback, useEffect, useState } from "react";
import MainBody from "../../../components/MainBody/MainBody";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  fileUpload,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../config";
import moment from "moment";
import { CalendarDatePicker } from "../../../components/CalendarDatePicker";
import BootstrapTable from "react-bootstrap-table-next";
import {
  formatNumber,
  formatPhone,
  formatSubcontractorName,
  pastFinancialdate,
} from "../../../lib/Misc";
import { Download, Home } from "lucide-react";
import { debounce } from "lodash";
import sortCaret from "../../../components/SortCaret";
import Pagination from "../../../components/Pagination";
import { useMediaQuery } from "../../../components/use-media-query";
import CommonFilters from "../../../components/CommonFilters";
import { useTrust } from "../../../lib/context/TrustContext";
import { useOrganization } from "../../../lib/context/OrganisationContext";
import { Card, CardContent } from "../../../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../components/ui/accordion";
import DropDownButton from "../../../components/DropDownButton";
import ContactStatementAction from "./ContactStatementAction";

const ContactStatement = ({ org = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("ASC");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const fromDate = query.get("from")
    ? new Date(query.get("from")?.split("-").reverse().join("-"))
    : "";
  const toDate = query.get("to")
    ? new Date(query.get("to")?.split("-").reverse().join("-"))
    : "";
  const { trustId: trustID } = useTrust(org);
  const [trustId, setTrustId] = useState(org ? "" : trustID);
  const [selectedDateRange, setSelectedDateRange] = useState(
    fromDate && toDate
      ? {
          from: fromDate,
          to: toDate,
        }
      : {
          from: pastFinancialdate[0],
          to: pastFinancialdate[1],
        }
  );

  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const { organizationData } = useOrganization();

  const [contactId, setContactId] = useState();

  const [trustListData, setTrustListData] = useState([]);

  const [activeTrustId, setActiveTrustId] = React.useState(null);

  useEffect(() => {
    setTrustId(trustID);
  }, [trustID]);

  const fetchTustStatementReportData = useCallback(async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      if (trustId?.length > 0) {
        trustId?.map((item, index) =>
          formData.append(`trust_ids[${index}]`, item)
        );
      }

      formData.append("type", "contact-statement-report");
      formData.append(
        "organization_id",
        organizationData?.organizationId ? organizationData?.organizationId : ""
      );
      formData.append(
        `from`,
        moment(selectedDateRange?.from).format("DD-MM-YYYY")
          ? moment(selectedDateRange?.from).format("DD-MM-YYYY")
          : ""
      );
      formData.append(
        `to`,
        moment(selectedDateRange?.to).format("DD-MM-YYYY")
          ? moment(selectedDateRange?.to).format("DD-MM-YYYY")
          : ""
      );

      const { data } = await fileUpload(
        `${API.ORG_GENERAL_LEDGER_REPORT}`,
        formData
      );
      setTrustListData(data?.data);
      let activetTust = data?.data?.filter((item) => item?.transactions === 1);

      if (activetTust?.[0]?.id !== undefined) {
        setActiveTrustId(activetTust?.[0]?.id);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setReportData([]);
    }
  }, [
    trustId,
    organizationData?.organizationId,
    selectedDateRange?.from,
    selectedDateRange?.to,
  ]);

  useEffect(() => {
    if (org) {
      fetchTustStatementReportData();
    }
  }, [org, fetchTustStatementReportData]);

  const handleAccordionChange = async (value) => {
    setReportData([]);
    if (!value) {
      setActiveTrustId(null);
      return;
    }
    setActiveTrustId(value);
  };

  const fetchContactItem = useCallback(
    async (passLimit, passPage, activeTrustId) => {
      try {
        setLoading(true);
        const formData = new FormData();

        formData.append(
          "trust_id",
          org && activeTrustId ? activeTrustId : trustId ? trustId : ""
        );
        formData.append(
          "limit",
          passLimit ? passLimit : limit ? limit : defaultLimitPagination
        );
        formData.append("page", passPage ? passPage : page ? page : 1);
        formData.append("search", searchParam ? searchParam : "");
        formData.append("sort_column", sortField ? sortField : "");
        formData.append("sort_order", sortOrder ? sortOrder : "");

        formData.append(
          "from",
          moment(selectedDateRange?.from).format("DD-MM-YYYY")
            ? moment(selectedDateRange?.from).format("DD-MM-YYYY")
            : ""
        );
        formData.append(
          "to",
          moment(selectedDateRange?.to).format("DD-MM-YYYY")
            ? moment(selectedDateRange?.to).format("DD-MM-YYYY")
            : ""
        );

        if (contactId?.length > 0) {
          contactId?.map((item, index) =>
            formData.append(`contact_ids[${index}]`, item)
          );
        }
        formData.append("export", 0);
        const { data } = await fileUpload(
          `${API.CONTACT_STATEMENT_REPORT}`,
          formData
        );
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        setReportData(data?.data?.data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setReportData([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      org,
      trustId,
      searchParam,
      sortField,
      sortOrder,
      selectedDateRange?.from,
      selectedDateRange?.to,
      contactId,
    ]
  );

  useEffect(() => {
    if (org) {
      if (activeTrustId) {
        fetchContactItem(defaultLimitPagination, 1, activeTrustId);
      }
    } else if (!org) {
      fetchContactItem();
    }
  }, [fetchContactItem, activeTrustId, org]);

  /**
   * Table Change For Sorting
   */
  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchContactItem(limit, 1, activeTrustId);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchContactItem(limit, page, activeTrustId);
  };
  // *SubAssets Table Row and Column Generation, Filtering and Sorting of Record - End

  useEffect(() => {
    if (!org) {
      navigate(
        `?tab=customer-statement&limit=${
          limit ? limit : defaultLimitPagination
        }&page=${page ? page : 1}&search=${
          searchParam ? searchParam : ""
        }&sort_column=${sortField ? sortField : ""}&sort_order=${
          sortOrder ? sortOrder : ""
        }&from=${
          moment(selectedDateRange?.from).format("DD-MM-YYYY")
            ? moment(selectedDateRange?.from).format("DD-MM-YYYY")
            : ""
        }&to=${
          moment(selectedDateRange?.to).format("DD-MM-YYYY")
            ? moment(selectedDateRange?.to).format("DD-MM-YYYY")
            : ""
        }`,
        {
          replace: true,
        }
      );
    }
  }, [
    limit,
    navigate,
    org,
    page,
    searchParam,
    selectedDateRange?.from,
    selectedDateRange?.to,
    sortField,
    sortOrder,
  ]);

  const reportGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: reportData[i]?.id ? reportData[i]?.id : "",
        name: formatSubcontractorName(reportData[i]),
        email: reportData[i]?.email ? reportData[i]?.email : "-",
        phone: reportData[i]?.phone ? formatPhone(reportData[i]?.phone) : "-",
        closing_balance: reportData[i]?.closing_balance
          ? reportData[i]?.closing_balance > 0
            ? `($${formatNumber(Number(reportData[i]?.closing_balance))})`
            : "$" +
              formatNumber(Number(Math.abs(reportData[i]?.closing_balance)))
          : "$0.00",
        action: (
          <ContactStatementAction
            contactId={reportData[i]?.id}
            contact={reportData[i]}
            trustId={
              org && activeTrustId ? activeTrustId : trustId ? trustId : ""
            }
          />
        ),
      });
    }
    return items;
  };

  const reportDatas = reportGenerator(reportData?.length);

  /**
   * Table Heading Data
   */
  const columns = [
    {
      dataField: "name",
      text: "Name",
      headerClasses: THClasses,
      classes: TDClasses,
      sort: true,
      sortCaret,
    },
    {
      dataField: "email",
      text: "Email Address",
      headerClasses: THClasses,
      classes: TDClasses,
      sort: true,
      sortCaret,
    },
    {
      dataField: "phone",
      text: "Phone",
      headerClasses: THClasses,
      classes: TDClasses,
      sort: true,
      sortCaret,
    },
    {
      dataField: "closing_balance",
      text: "Closing Balance",
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const arrayRemove = (arr, value) => {
    return arr.filter(function (ele) {
      return ele !== value;
    });
  };

  // eslint-disable-next-line no-unused-vars
  const selectRow = {
    mode: "checkbox",
    clickToSelect: false,
    classes: TDClasses,
    selected: selectedRowIds,
    // nonSelectable: xeroData?.connection_status === 1 ? [] : notSelectedRowIds,
    onSelectAll: (row, isSelect) => {
      if (reportData?.length > 0) {
        if (row) {
          const ids =
            isSelect &&
            isSelect?.length > 0 &&
            isSelect?.map((item) => {
              return item?.key;
            });
          setSelectedRowIds(selectedRowIds ? selectedRowIds.concat(ids) : ids);
        } else {
          const removableData =
            isSelect &&
            isSelect?.length > 0 &&
            isSelect.map(function (data) {
              return data?.key;
            });
          setSelectedRowIds(
            selectedRowIds
              ? selectedRowIds.filter((item) => !removableData.includes(item))
              : []
          );
        }
      }
    },
    onSelect: (row, isSelect) => {
      if (isSelect) {
        setSelectedRowIds((keys) => [...keys, row?.key]);
      } else {
        setSelectedRowIds(arrayRemove(selectedRowIds, row?.key));
      }
    },
  };

  const handleExport = async (format) => {
    try {
      setLoading(true);
      const formData = new FormData();

      // if (selectedRowIds?.length > 0) {
      //   for (let i = 0; i < selectedRowIds?.length; i++) {
      //     formData.append(`contact_ids[${i}]`, selectedRowIds[i]);
      //   }
      // }

      if (org) {
        if (trustId?.length > 0) {
          trustId?.map((item, index) =>
            formData.append(`trust_ids[${index}]`, item)
          );
        }

        formData.append(
          "organization_id",
          organizationData?.organizationId
            ? organizationData?.organizationId
            : ""
        );
      } else if (!org) {
        formData.append(`trust_id`, trustId ? trustId : "");
      }

      formData.append(
        "from",
        moment(selectedDateRange?.from).format("DD-MM-YYYY")
          ? moment(selectedDateRange?.from).format("DD-MM-YYYY")
          : ""
      );
      formData.append(
        "to",
        moment(selectedDateRange?.to).format("DD-MM-YYYY")
          ? moment(selectedDateRange?.to).format("DD-MM-YYYY")
          : ""
      );
      formData.append("format", format);

      if (contactId?.length > 0) {
        contactId?.map((item, index) =>
          formData.append(`contact_ids[${index}]`, item)
        );
      }
      formData.append("export", 1);

      const { data } = await fileUpload(
        `${API.CONTACT_STATEMENT_REPORT}`,
        formData
      );
      const export_report_path = data?.data?.file;
      if (export_report_path) {
        var a = document.createElement("a");
        a.href = export_report_path;
        var file = export_report_path.split("/");
        a.setAttribute(
          "download",
          file[file.length - 1] || "transactionsreport.pdf"
        );
        a.setAttribute("target", "_blank");
        document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
        a.click();
        a.remove();
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <MainBody
        search={org ? false : true}
        actionAlign={org ? "end" : ""}
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          <>
            <CalendarDatePicker
              date={selectedDateRange}
              onDateSelect={setSelectedDateRange}
              variant="outline"
              align={isDesktop ? "end" : "start"}
              disabledDate={(date) => date > new Date()}
            />

            <CommonFilters
              trust={org}
              trustId={trustId}
              multiTrust={org}
              setTrustId={setTrustId}
              vendor={false}
              multiVendor={true}
              multiContact={true}
              contact={true}
              setContactId={setContactId}
              contactId={contactId}
            />

            <DropDownButton
              items={[
                {
                  label: "PDF",
                  onClick: () => handleExport("pdf"),
                  disabled: loading,
                },
                {
                  label: "Excel",
                  onClick: () => handleExport("xlsx"),
                  disabled: loading,
                },
              ]}
              text={
                <div className="flex gap-2 items-center">
                  Export <Download size={18} />
                </div>
              }
            />
          </>
        }
        expandList={true}
        className="!p-0 !bg-background"
        loading={loading}
      >
        {org && trustListData?.length > 0 ? (
          <>
            <Accordion
              type="single"
              collapsible
              value={activeTrustId}
              onValueChange={handleAccordionChange}
              className="w-full"
            >
              {trustListData.map((item) => {
                const trustLabel = item?.business_name
                  ? item?.business_name
                  : item?.first_name
                  ? item?.first_name + " " + item?.last_name
                  : item?.last_name;

                return (
                  <AccordionItem
                    key={item?.id}
                    value={item?.id}
                    className="border-none mb-2"
                  >
                    <AccordionTrigger className="text-base font-medium bg-webSecondary hover:bg-webSecondary text-white dark:bg-muted px-4 py-2 rounded-md dark:hover:bg-muted/70">
                      <div className="flex items-center justify-between w-full pr-3">
                        {trustLabel}
                        <div className="flex gap-3">
                          <div>
                            Opening Balance:{" "}
                            <span>
                              {item?.opening_balance
                                ? item?.opening_balance > 0
                                  ? `($${formatNumber(
                                      Number(item?.opening_balance)
                                    )})`
                                  : `$${formatNumber(
                                      Math.abs(Number(item?.opening_balance))
                                    )}`
                                : "$0.00"}
                            </span>
                          </div>
                          <div>
                            Closing Balance:{" "}
                            <span>
                              {item?.closing_balance
                                ? item?.closing_balance > 0
                                  ? `($${formatNumber(
                                      Number(item?.closing_balance)
                                    )})`
                                  : `$${formatNumber(
                                      Math.abs(Number(item?.closing_balance))
                                    )}`
                                : "$0.00"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      {" "}
                      <div className="custom-table mt-2">
                        <div
                          className={
                            "table-body-class sm:max-h-[600px] rounded-md"
                          }
                        >
                          <BootstrapTable
                            keyField="key"
                            remote
                            data={reportDatas}
                            columns={columns}
                            // selectRow={selectRow}
                            onTableChange={handleTableChange}
                            rowClasses={(row) => {
                              if (row?.balanceRow) {
                                return `${TRClasses} font-bold dark:bg-muted bg-webSecondary dark:hover:!bg-muted hover:!bg-webSecondary dark:text-foreground text-white`;
                              }
                              return TRClasses;
                            }}
                            noDataIndication="No Data Found"
                            classes={TableClasses}
                            wrapperClasses={TableWrapClasses}
                          />
                        </div>
                        <Pagination
                          total={total}
                          limit={parseInt(limit)}
                          currentPage={page}
                          updateLimit={handleUpdateLimit}
                          updatePage={handleChangePage}
                          from={from}
                          to={to}
                        />
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </>
        ) : !org ? (
          <div className="custom-table">
            <div className={"table-body-class"}>
              <BootstrapTable
                keyField="key"
                remote
                data={reportDatas}
                columns={columns}
                // selectRow={selectRow}
                onTableChange={handleTableChange}
                rowClasses={(row) => {
                  if (row?.balanceRow) {
                    return `${TRClasses} font-bold dark:bg-muted bg-webSecondary dark:hover:!bg-muted hover:!bg-webSecondary dark:text-foreground text-white`;
                  }
                  return TRClasses;
                }}
                noDataIndication="No Data Found"
                classes={TableClasses}
                wrapperClasses={TableWrapClasses}
              />
            </div>
            <Pagination
              total={total}
              limit={parseInt(limit)}
              currentPage={page}
              updateLimit={handleUpdateLimit}
              updatePage={handleChangePage}
              from={from}
              to={to}
            />
          </div>
        ) : (
          <>
            <div className="flex h-full items-center justify-center p-4">
              <Card className="max-w-md w-full shadow-lg text-center">
                <CardContent className="flex flex-col items-center gap-4 py-10">
                  <Home className="h-12 w-12 text-webPrimary" />
                  <h2 className="text-2xl font-semibold ">
                    Please Select Trust{" "}
                  </h2>
                  <p>To get Report Data.</p>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </MainBody>
    </div>
  );
};

export default ContactStatement;
